import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const HandEyeCirclePlay = () => {
  const canvasRef    = useRef(null);
  const requestRef   = useRef();
  const navigate     = useNavigate();
  const penaltyRef   = useRef(0);
  const lastXRef     = useRef(0);
  const inputLockRef = useRef(true);

  const [userPath,  setUserPath]  = useState([]);
  const [finishMsg, setFinishMsg] = useState(null);

  const ballRef        = useRef({ x: 0, y: 0, size: 12 });
  const centerRef      = useRef({ x: 0, y: 0 });
  const outerRadiusRef = useRef(0);
  const innerRadiusRef = useRef(0);

  const startTimeRef     = useRef(null);
  const nextSampleRef    = useRef(0);
  const lastTimeRef      = useRef(Date.now());
  const circleCoordsRef  = useRef([]);
  const endLineCoordsRef = useRef([]);

  const angleRef = useRef({ last: null, cumulative: 0, lapCompleted: false });

  const endLineRef     = useRef(false);
  const finishedRef    = useRef(false);
  const gameStartedRef = useRef(false);

  const axisCenter = useRef({ x: 0, y: 0 });
  const calibrated = useRef(false);

  const DEADZONE          = 0.35;
  const TRIGGER_THRESHOLD = 0.08;

  useEffect(() => {
    init();
    calibrated.current     = false;
    gameStartedRef.current = false;
    finishedRef.current    = false;
    endLineRef.current     = false;
    inputLockRef.current   = true;
    setTimeout(() => { inputLockRef.current = false; }, 300);
    requestRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(requestRef.current);
  }, []);

  const init = () => {
    const canvas = canvasRef.current;

    // ── KEY FIX: innerWidth/innerHeight matches CSS 100vw/100vh exactly ──
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;

    const w    = canvas.width;
    const h    = canvas.height;
    const size = Math.min(w, h);

    centerRef.current      = { x: w / 2, y: h / 2 };
    outerRadiusRef.current = size * 0.35;
    innerRadiusRef.current = size * 0.25;

    const mid = (outerRadiusRef.current + innerRadiusRef.current) / 2;

    ballRef.current    = { x: centerRef.current.x, y: centerRef.current.y - mid, size: 12, color: "blue" };
    lastXRef.current   = centerRef.current.x;
    calibrated.current = false;
    penaltyRef.current = 0;
    lastTimeRef.current = Date.now();
    setUserPath([]);
  };

  const getInput = () => {
    const gp = navigator.getGamepads()[0];
    if (!gp) return null;

    let rawX = gp.axes[2] || 0;
    let rawY = gp.axes[1] || 0;

    if (!calibrated.current) { axisCenter.current = { x: 0, y: 0 }; calibrated.current = true; }

    let dx = rawX - axisCenter.current.x;
    let dy = rawY - axisCenter.current.y;

    if (Math.abs(dx) < DEADZONE) dx = 0;
    if (Math.abs(dy) < DEADZONE) dy = 0;
    dx = Math.max(-1, Math.min(1, dx));
    dy = Math.max(-1, Math.min(1, dy));

    return { dx, dy, r2: gp.buttons[7]?.value || 0, l2: gp.buttons[6]?.value || 0 };
  };

  const loop = () => { update(); draw(); requestRef.current = requestAnimationFrame(loop); };

  const update = () => {
    const input = getInput();
    if (!input) return;

    const ball   = ballRef.current;
    const canvas = canvasRef.current;
    const now    = Date.now();
    const w      = canvas.width;
    const h      = canvas.height;

    const rawDt = now - lastTimeRef.current;
    const dt    = Math.min(rawDt / 16.67, 2);
    lastTimeRef.current = now;

    let dx = Number(input.dx.toFixed(2));
    let dy = Number(input.dy.toFixed(2));
    if (Math.abs(dx) < 0.25) dx = 0;
    if (Math.abs(dy) < 0.25) dy = 0;

    const isRealInput = dx !== 0 || dy !== 0;

    if (!gameStartedRef.current && isRealInput) {
      startTimeRef.current   = now;
      nextSampleRef.current  = now + 250;
      gameStartedRef.current = true;
      angleRef.current       = { last: null, cumulative: 0, lapCompleted: false };
      setUserPath([]);
      recordPosition();
    }

    const accel = 1 + input.r2 * 2;

    if (input.l2 > TRIGGER_THRESHOLD) {
      // BRAKE
    } else if (isRealInput) {
      ball.x += dx * 6 * accel * dt;
      ball.y += dy * 6 * accel * dt;
    }

    // clamp — canvas coords match visible screen exactly
    const r = ball.size;
    ball.x = Math.max(r, Math.min(w - r, ball.x));
    ball.y = Math.max(r, Math.min(h - r, ball.y));

    if (gameStartedRef.current && now >= nextSampleRef.current) {
      recordPosition();
      nextSampleRef.current = now + 250;
    }

    if (gameStartedRef.current) endLineRef.current = true;

    const cx   = centerRef.current.x;
    const cy   = centerRef.current.y;
    const dist = Math.sqrt((ball.x-cx)**2 + (ball.y-cy)**2);
    if (dist > outerRadiusRef.current || dist < innerRadiusRef.current) penaltyRef.current += rawDt / 1000;

    const crossedLine = lastXRef.current < cx && ball.x >= cx;
    lastXRef.current  = ball.x;

    if (endLineRef.current && angleRef.current.lapCompleted && crossedLine && !finishedRef.current) {
      finishedRef.current = true;
      finishGame();
    }
  };

  const recordPosition = () => {
    const ball = ballRef.current;
    setUserPath(prev => { const updated = [...prev, [ball.x, ball.y]]; checkAngle(ball.x, ball.y); return updated; });
  };

  const checkAngle = (x, y) => {
    const cx = centerRef.current.x, cy = centerRef.current.y;
    let angle = (Math.atan2(y-cy, x-cx) * 180 / Math.PI + 360) % 360;
    const state = angleRef.current;

    if (state.last === null) { state.last = angle; state.start = angle; state.cumulative = 0; return; }

    let delta = angle - state.last;
    if (delta <= -180) delta += 360;
    else if (delta > 180) delta -= 360;

    if (delta > 0) state.cumulative += delta;
    else if (delta < 0) state.cumulative = Math.max(0, state.cumulative + delta);
    state.last = angle;

    if (!state.lapCompleted && state.cumulative >= 330) state.lapCompleted = true;
  };

  const draw = () => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d");
    const w      = canvas.width, h = canvas.height;

    ctx.clearRect(0, 0, w, h);

    const cx = centerRef.current.x, cy = centerRef.current.y;
    const outer = outerRadiusRef.current, inner = innerRadiusRef.current;

    ctx.strokeStyle = angleRef.current.lapCompleted ? "green" : "black";
    ctx.lineWidth   = 3;
    ctx.beginPath(); ctx.arc(cx, cy, outer, 0, Math.PI*2); ctx.stroke();
    ctx.beginPath(); ctx.arc(cx, cy, inner, 0, Math.PI*2); ctx.stroke();

    if (endLineRef.current) {
      const a = -Math.PI / 2;
      ctx.beginPath();
      ctx.moveTo(cx + inner*Math.cos(a), cy + inner*Math.sin(a));
      ctx.lineTo(cx + outer*Math.cos(a), cy + outer*Math.sin(a));
      ctx.strokeStyle = "red"; ctx.lineWidth = 4; ctx.stroke();
    }

    const ball = ballRef.current;
    ctx.beginPath(); ctx.arc(ball.x, ball.y, ball.size, 0, Math.PI*2);
    ctx.fillStyle = angleRef.current.lapCompleted ? "green" : "blue"; ctx.fill();
  };

  const finishGame = () => {
    const endTime = Date.now();
    const testTime = (endTime - startTimeRef.current) / 1000;
    const penaltyTime = penaltyRef.current || 0;
    const totalTime   = testTime + penaltyTime;

    const result = {
      GameName: "Circle Path", userPath,
      testTime: Number(testTime.toFixed(2)), penaltyTime: Number(penaltyTime.toFixed(2)), totalTime: Number(totalTime.toFixed(2)),
      StartBallX: ballRef.current.x, StartBallY: ballRef.current.y,
      circleCoordsList: circleCoordsRef.current || [], endLineCoords: endLineCoordsRef.current || [],
      ballSize: ballRef.current.size
    };

    setFinishMsg({ time: result.totalTime, testTime: result.testTime, penaltyTime: result.penaltyTime, score: result });

    axios.post("/save-handeye", {
      email: localStorage.getItem("user_email"), device: "Gamepad", data: { CirclePathData: result }
    }).catch(err => console.error("Save error:", err));
  };

  return (
    <div style={{ overflow: "hidden", width: "100vw", height: "100vh", margin: 0, padding: 0 }}>
      <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", display: "block", background: "#fff" }} />

      {finishMsg && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }}>
          <div style={{ background: "white", padding: "40px 48px", borderRadius: "16px", textAlign: "center", minWidth: "300px", boxShadow: "0 8px 32px rgba(0,0,0,0.3)" }}>
            <h2 style={{ margin: "0 0 8px", fontSize: "24px", color: "#222" }}>Test Complete!</h2>
            <p style={{ color: "#666", marginBottom: "20px", fontSize: "15px" }}>Circle Path finished</p>
            <div style={{ background: "#f5f5f5", borderRadius: "10px", padding: "16px", marginBottom: "24px" }}>
              <p style={{ margin: "4px 0", fontSize: "14px", color: "#555" }}>Test time: <b>{finishMsg.testTime}s</b></p>
              <p style={{ margin: "4px 0", fontSize: "14px", color: "#d32f2f" }}>Penalty time: <b>{finishMsg.penaltyTime}s</b></p>
              <p style={{ margin: "12px 0 0", fontSize: "20px", fontWeight: "bold", color: "#222" }}>Total: {finishMsg.time}s</p>
            </div>
            <button onClick={() => { setFinishMsg(null); navigate("/hand-eye/curve", { state: { currentScore: finishMsg.time } }); }}
              style={{ padding: "12px 40px", background: "#4CAF50", color: "white", border: "none", borderRadius: "10px", fontSize: "18px", fontWeight: "bold", cursor: "pointer" }}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HandEyeCirclePlay;
